import {
  IExperienceTemplate,
  InternalStorageInstructionType,
  PARTICIPANT_ACTOR_KEY,
  SubTaskType
} from '@dadohr/shared/domainTypes/experience';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { UnsafeTFunction } from '../../i18next';
import {
  AutomationBuilderActionValidationStateItem,
  hasValidActionState,
  validateActions
} from '../../features/Experiences/components/Builders/AutomationBuilder/validation';
import {
  hasValidBlockState,
  TaskBuilderBlockValidationStateItem,
  validateBlocks
} from '../../features/Experiences/components/Builders/TaskBuilder/validation';
import { TaskBuilderContext } from '../../features/Experiences/components/Builders/TaskBuilder/types';
import { isValidTiming } from './timing';
import { getAllMessageTemplates } from './template';

export type TemplateValidationError = {
  itemType: 'TASK' | 'AUTOMATION' | 'MESSAGE';
  itemId: string;
  itemTitle: string;
  errors: string[];
};

/**
 * `?? []` only catches null/undefined — a string like `"foo"` passes
 * through as truthy, then `.filter` / `.map` / `.reduce` on that string
 * crashes. Firestore lets admins (and accidental writes) replace any
 * value with anything, so guard explicitly with `Array.isArray`.
 */
const safeArr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/**
 * Optional telemetry callback passed to every `normalize*` function.
 * Fires ONLY when the normalizer actually healed something — never on
 * idempotent passes (ref-equal returns). Receives the normalizer's
 * identifier + a count of blocks/actions touched.
 *
 * Telemetry-only — receives NO raw user-data. Callers wire this to
 * emit HEAL_ON_LAUNCH_APPLIED so we can see which normalizers fire
 * most often (informs where to tighten AI writers).
 *
 * RESILIENCE GUARANTEE: every normalizer invokes this callback inside
 * its own try/catch. A throw inside the callback CANNOT corrupt the
 * heal-on-launch behavior or block the template save. Validator
 * resilience > analytics.
 */
export interface NormalizeOptions {
  onHealed?: (event: { normalizerName: string; itemsHealed: number }) => void;
}

const safeInvokeOnHealed = (
  opts: NormalizeOptions | undefined,
  normalizerName: string,
  itemsHealed: number
): void => {
  if (!opts?.onHealed) return;
  try {
    opts.onHealed({ normalizerName, itemsHealed });
  } catch (err) {
    // NEVER let a telemetry callback affect normalizer behavior.
    console.warn(`[${normalizerName}] onHealed callback threw — swallowed`, err);
  }
};

const buildTaskBuilderContext = (
  tpl: IExperienceTemplate
): TaskBuilderContext => {
  // Optional chain everything — `validateEntireTemplate` is the
  // bend-back-into-shape boundary, and the user explicitly tests by
  // deleting random Firestore fields. A missing top-level field on
  // `tpl` must not crash context construction; downstream validators
  // each guard the field they read.
  const safe = (tpl ?? ({} as IExperienceTemplate)) as IExperienceTemplate;
  return {
    templateId: safe.id,
    orgId: safe.orgId,
    title: safe.title,
    adminTitle: safe.adminTitle,
    contacts: safe.contacts,
    notificationSender: safe.notificationSender,
    status: safe.status,
    supportingActors: safe.supportingActors,
    anchor: safe.anchor,
    tz: safe.tz,
    orgOverrides: safe.overrides?.org || null,
    tasks: safe.tasks
  };
};

/**
 * `task.actor` and `message.actor` must be either `participant` or a key
 * that exists in the template's `supportingActors` map. The AI tool layer
 * rejects unknown actors at create time, but a dangling reference can be
 * introduced by editing an actor out of supportingActors later — those
 * silently degrade the FE without a check here.
 */
const validateActorReference = (
  actor: string | undefined | null,
  supportingActors: IExperienceTemplate['supportingActors'],
  t: UnsafeTFunction
): string | null => {
  if (!actor) return t('Assignee is required');
  if (actor === PARTICIPANT_ACTOR_KEY) return null;
  if (supportingActors && Object.prototype.hasOwnProperty.call(supportingActors, actor)) {
    return null;
  }
  const known = supportingActors ? Object.keys(supportingActors) : [];
  return t(
    `Assigned actor "${actor}" doesn't exist on this template. Known actors: ${
      known.length ? known.join(', ') : 'none'
    }. Add the actor in Settings or pick an existing one.`
  );
};

/**
 * Each `task.audiences[i].blocks` entry must reference a real block id on
 * the task. The reverse — every block must appear in at least one audience
 * — is also an invariant: a block that no audience owns is invisible to
 * the participant and is almost certainly a mistake. AI writes both
 * directions correctly today; manual edits or partial deletes are the
 * usual way these get out of sync.
 */
const validateAudienceBlockLinkage = (
  task: IExperienceTemplate['tasks'][number],
  t: UnsafeTFunction
): string[] => {
  const errors: string[] = [];
  const blocks = safeArr<{ id?: string; type?: string; data?: unknown }>(
    task?.blocks
  ).filter((b) => b && typeof b === 'object');
  const blockIds = new Set(blocks.map((b) => b.id).filter(Boolean));
  const audiences = safeArr<{
    id?: string;
    name?: string;
    blocks?: unknown;
    rule?: unknown;
  }>(task?.audiences).filter((a) => a && typeof a === 'object');

  const referencedBlockIds = new Set<string>();
  for (const audience of audiences) {
    const audBlocks = Array.isArray(audience.blocks) ? audience.blocks : [];
    for (const blockId of audBlocks) {
      if (!blockId) continue;
      if (!blockIds.has(blockId)) {
        errors.push(
          t(
            `Audience "${audience.name || audience.id || '<unnamed>'}" references a missing block (id ${blockId}). Open the task and reassign or remove the orphan entry.`
          )
        );
      }
      referencedBlockIds.add(blockId);
    }
  }

  for (const block of blocks) {
    if (!block.id) continue;
    if (!referencedBlockIds.has(block.id)) {
      const data = (block as { data?: { title?: string; label?: string } }).data;
      const label = data?.title ?? data?.label ?? block.type ?? '<untyped>';
      errors.push(
        t(
          `Subtask "${label}" isn't assigned to any audience — participants won't see it. Open the task and add it to at least one audience.`
        )
      );
    }
  }

  return errors;
};

/**
 * The built-in `address` user field key. Standard fields live un-prefixed in
 * storage destinations; custom fields use the `customFields.<key>` form
 * produced by `toCustomFieldKey` in @dadohr/shared.
 */
const CUSTOM_FIELD_PREFIX = 'customFields.';
const BUILTIN_ADDRESS_FIELD_KEY = 'address';

/**
 * A "Provide Address" subtask (QUESTION block with `data.question.type ===
 * 'address'`) emits a structured Address value when the participant submits.
 * Saving that into a non-address Dado field silently coerces to JSON or
 * loses sub-fields downstream, so the destination must point at an
 * address-typed user field. The destination dropdown shows ALL fields with
 * no filter, so picking a string field (firstName, role, ...) is an easy
 * misclick — and AI tools that future-generate address questions have the
 * same hole.
 */
const validateAddressQuestionStorages = (
  block: { type?: string; data?: any },
  customFields: CustomUserField[],
  t: UnsafeTFunction
): string[] => {
  if (!block || typeof block !== 'object') return [];
  if (block.type !== SubTaskType.QUESTION) return [];
  const question = block.data?.question;
  if (question?.type !== 'address') return [];
  const storagesRaw = block.data?.storages;
  const storages: Array<{ type?: string; field?: string }> = Array.isArray(
    storagesRaw
  )
    ? storagesRaw
    : [];
  const fields = Array.isArray(customFields) ? customFields : [];

  const errors: string[] = [];
  for (const storage of storages) {
    if (!storage || typeof storage !== 'object') continue;
    if (storage.type !== InternalStorageInstructionType.USER_PROFILE) continue;
    if (!storage.field || typeof storage.field !== 'string') continue;

    let resolvedType: string | null = null;
    if (storage.field === BUILTIN_ADDRESS_FIELD_KEY) {
      resolvedType = 'address';
    } else if (storage.field.startsWith(CUSTOM_FIELD_PREFIX)) {
      const customKey = storage.field.slice(CUSTOM_FIELD_PREFIX.length);
      resolvedType = fields.find((f) => f?.key === customKey)?.type ?? null;
    } else {
      // Standard non-address field (firstName, role, hireDate, ...). The
      // standardUserFields list lives in apps/services/user; we don't need to
      // re-derive types here — any non-address standard key is wrong.
      resolvedType = 'non-address';
    }

    if (resolvedType !== 'address') {
      errors.push(
        t(
          `"Provide Address" can only save to address-typed Dado fields. The destination "${storage.field}" is ${resolvedType ?? 'unknown'} — pick an address-typed user field (or add one in Settings → Custom Fields).`
        )
      );
    }
  }
  return errors;
};

/**
 * Subtask block types — these are the blocks that own a `hooks` field on
 * the BE schema. Content blocks (TEXT, IMAGE, …) and layout blocks
 * (COLUMNS) don't carry hooks and must NOT be modified by the normalizer
 * below.
 */
const SUBTASK_BLOCK_TYPES: ReadonlySet<string> = new Set(Object.values(SubTaskType));

/**
 * Retro-fix the canonical `{ onStart: [], onComplete: [] }` hooks shape
 * on any subtask block missing it. Legacy templates predate hooks being
 * mandatory and ship with no `hooks` field at all — the FE validator
 * tripped on `b.hooks.onComplete` (now null-guarded) and a bunch of
 * BlockSettings dialogs read `value.hooks.onComplete` directly.
 *
 * Healing on the launch path (then persisting via the save that
 * already runs there) means each ROUGH_DRAFT → ACTIVE transition
 * cleans up its own template; the bug class disappears as templates
 * get touched. No behavior change for already-canonical blocks —
 * returns the same object reference when nothing needs healing so
 * Firestore doesn't see a spurious write.
 */
export const normalizeTaskBlockHooks = (
  tpl: IExperienceTemplate,
  opts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextTasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks).map((task) => {
    const blocks = safeArr<{ type?: string; data?: any; hooks?: any }>(task?.blocks);
    let taskTouched = false;
    const nextBlocks = blocks.map((block: any) => {
      if (!SUBTASK_BLOCK_TYPES.has(block?.type)) return block;
      if (block.hooks && typeof block.hooks === 'object') {
        // Already has hooks — leave as-is. Don't re-shape inner arrays
        // either; the per-field validators handle malformed inner
        // values, and we want this normalizer to be conservative.
        return block;
      }
      taskTouched = true;
      itemsHealed += 1;
      return { ...block, hooks: { onStart: [], onComplete: [] } };
    });
    if (!taskTouched) return task;
    touched = true;
    return { ...task, blocks: nextBlocks };
  });
  if (touched) safeInvokeOnHealed(opts, 'normalizeTaskBlockHooks', itemsHealed);
  return touched ? { ...tpl, tasks: nextTasks } : tpl;
};

/**
 * Subtask types whose canonical schema uses `data.label`. The AI tool
 * earlier wrote `data.title` for all of these, which crashed the FE
 * renderer (instance.ts:940) when interpolateExperienceText received
 * `undefined`. Heal-on-launch migrates `title` → `label` so the
 * persisted shape matches what the renderer expects, and the bug class
 * disappears as templates pass through the launch flow.
 *
 * DOCUMENT_READ stays `data.title` per its schema. CONFIRM stays
 * `data.label` already. QUESTION has its own `data.question.label`
 * nested shape and is untouched here.
 */
const LABEL_SUBTASK_TYPES: ReadonlySet<string> = new Set([
  SubTaskType.WATCH,
  SubTaskType.VISIT,
  SubTaskType.SUBMIT,
  SubTaskType.CALENDLY_INVITE
]);

export const normalizeSubtaskLabelField = (
  tpl: IExperienceTemplate,
  opts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextTasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks).map((task) => {
    const blocks = safeArr<{ type?: string; data?: any; hooks?: any }>(task?.blocks);
    let taskTouched = false;
    const nextBlocks = blocks.map((block: any) => {
      if (!LABEL_SUBTASK_TYPES.has(block?.type)) return block;
      const data = block.data;
      if (!data || typeof data !== 'object') return block;
      // Already has label — leave the block alone. We only migrate
      // when label is missing AND title is present, so a hand-edited
      // template that's already canonical doesn't trigger a write.
      if (typeof data.label === 'string') return block;
      if (typeof data.title !== 'string') return block;
      taskTouched = true;
      itemsHealed += 1;
      const { title, ...rest } = data;
      return { ...block, data: { ...rest, label: title } };
    });
    if (!taskTouched) return task;
    touched = true;
    return { ...task, blocks: nextBlocks };
  });
  if (touched) safeInvokeOnHealed(opts, 'normalizeSubtaskLabelField', itemsHealed);
  return touched ? { ...tpl, tasks: nextTasks } : tpl;
};

/**
 * AI-generated CREATE_MEETING blocks have shipped with only `data.title`
 * — `pplToAdd` / `description` / `anchor` / `at` / `repeat` /
 * `createLinksFor` all missing. The FE validator crashed on the first
 * `.length` / `.type` dereference, silently killing Check & Launch.
 * Validator is now resilient (see TaskBuilder/validation.ts), but heal
 * the persisted shape too so subsequent saves don't carry the gap
 * forward. Empty `pplToAdd` / `createLinksFor` are intentional — they
 * surface user-actionable validator errors instead of papering over the
 * admin's need to pick attendees and a calendar service.
 *
 * Anchor defaults to AFTER_EXPERIENCE_START_DATE to match what the
 * TaskBuilder uses for a freshly-added block (see emptyBlock in
 * apps:.../TaskBuilder/index.tsx#L2329).
 */
export const normalizeCreateMeetingBlock = (
  tpl: IExperienceTemplate,
  opts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextTasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks).map((task) => {
    const blocks = safeArr<{ type?: string; data?: any; hooks?: any }>(task?.blocks);
    let taskTouched = false;
    const nextBlocks = blocks.map((block: any) => {
      if (block?.type !== SubTaskType.CREATE_MEETING) return block;
      const data = (block.data ?? {}) as Record<string, unknown>;
      const patched: Record<string, unknown> = { ...data };
      let changed = false;
      if (typeof patched.title !== 'string') {
        patched.title = '';
        changed = true;
      }
      if (typeof patched.description !== 'string') {
        patched.description = '';
        changed = true;
      }
      if (!Array.isArray(patched.pplToAdd)) {
        patched.pplToAdd = [];
        changed = true;
      }
      const anchor = patched.anchor as { type?: unknown } | null | undefined;
      if (!anchor || typeof anchor.type !== 'string') {
        patched.anchor = {
          type: 'AFTER_EXPERIENCE_START_DATE',
          lowerBoundary: 0
        };
        changed = true;
      }
      if (!patched.at || typeof patched.at !== 'object') {
        patched.at = { hour: 9, minute: 0, duration: 30 * 60 * 1000 };
        changed = true;
      }
      if (!patched.repeat || typeof patched.repeat !== 'object') {
        patched.repeat = { enabled: false, frequency: 'WEEKLY' };
        changed = true;
      }
      if (!Array.isArray(patched.createLinksFor)) {
        patched.createLinksFor = [];
        changed = true;
      }
      if (!changed) return block;
      taskTouched = true;
      itemsHealed += 1;
      return { ...block, data: patched };
    });
    if (!taskTouched) return task;
    touched = true;
    return { ...task, blocks: nextBlocks };
  });
  if (touched) safeInvokeOnHealed(opts, 'normalizeCreateMeetingBlock', itemsHealed);
  return touched ? { ...tpl, tasks: nextTasks } : tpl;
};

/**
 * AI-generated DOCUSIGN_ENVELOPE_SIGN blocks shipped with `data:
 * { title: 'x' }` only because the writer (buildSubtaskBlock) had no
 * branch for the type and fell through to the catch-all. The shared
 * denormalizer at packages/shared/.../denormalize/subTask.ts:369
 * reads `sTpl.data.description.t` directly with no optional chain —
 * an incomplete shape crashed every consumer (employee app, builder,
 * execution report) before any render code ran. Writer is now fixed
 * (subtasks.ts:170-181) so new emits are canonical, but heal the
 * persisted shape too so legacy bad templates stop crashing.
 *
 * Empty `description` (Markdown { v: 1, t: '' }) renders blank;
 * empty `resourceId` triggers the admin to attach the envelope
 * resource in the UI.
 */
export const normalizeDocuSignEnvelopeSignBlock = (
  tpl: IExperienceTemplate,
  opts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextTasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks).map((task) => {
    const blocks = safeArr<{ type?: string; data?: any; hooks?: any }>(task?.blocks);
    let taskTouched = false;
    const nextBlocks = blocks.map((block: any) => {
      if (block?.type !== SubTaskType.DOCUSIGN_ENVELOPE_SIGN) return block;
      const data = (block.data ?? {}) as Record<string, unknown>;
      const patched: Record<string, unknown> = { ...data };
      let changed = false;
      const description = patched.description as { v?: unknown; t?: unknown } | null | undefined;
      if (!description || typeof description !== 'object' || typeof description.t !== 'string') {
        patched.description = { v: 1, t: '' };
        changed = true;
      }
      if (typeof patched.resourceId !== 'string') {
        patched.resourceId = '';
        changed = true;
      }
      if (!changed) return block;
      taskTouched = true;
      itemsHealed += 1;
      return { ...block, data: patched };
    });
    if (!taskTouched) return task;
    touched = true;
    return { ...task, blocks: nextBlocks };
  });
  if (touched) safeInvokeOnHealed(opts, 'normalizeDocuSignEnvelopeSignBlock', itemsHealed);
  return touched ? { ...tpl, tasks: nextTasks } : tpl;
};

/**
 * AI-generated SWAG_UP_CHOICE blocks have shipped with `data` missing
 * the canonical `{ choices, opts, swagUpContactEmails }` shape — empty
 * data, missing `choices`, missing `opts.hidePhotos`. The TaskBuilder
 * renderer (Messages.tsx > SubTaskBlockSwagUpChoice) dereferences
 * `value.data.question.choices.length` after the denormalizer wraps
 * `data` in `{ question, answer }`, and an incomplete shape crashes
 * the render with "Cannot read properties of undefined (reading
 * 'length')". Render is now defensive, but the persisted shape needs
 * to converge to canonical so subsequent saves don't carry the gap
 * forward and so the validator's specific "Please select some swag to
 * choose from" reaches the user.
 *
 * Empty `choices` and empty `swagUpContactEmails` are intentional —
 * they surface user-actionable validator errors instead of papering
 * over the admin's need to pick swag and a contact.
 */
export const normalizeSwagUpChoiceBlock = (
  tpl: IExperienceTemplate,
  // Named `normalizeOpts` (not `opts`) here to avoid colliding with
  // the SWAG_UP_CHOICE block's own `data.opts` local below.
  normalizeOpts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextTasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks).map((task) => {
    const blocks = safeArr<{ type?: string; data?: any; hooks?: any }>(task?.blocks);
    let taskTouched = false;
    const nextBlocks = blocks.map((block: any) => {
      if (block?.type !== SubTaskType.SWAG_UP_CHOICE) return block;
      const data = (block.data ?? {}) as Record<string, unknown>;
      const patched: Record<string, unknown> = { ...data };
      let changed = false;
      if (!Array.isArray(patched.choices)) {
        patched.choices = [];
        changed = true;
      }
      const opts = patched.opts as { hidePhotos?: unknown } | null | undefined;
      if (!opts || typeof opts !== 'object') {
        patched.opts = { hidePhotos: false };
        changed = true;
      } else if (typeof opts.hidePhotos !== 'boolean') {
        patched.opts = { ...opts, hidePhotos: false };
        changed = true;
      }
      if (!Array.isArray(patched.swagUpContactEmails)) {
        patched.swagUpContactEmails = [];
        changed = true;
      }
      if (!changed) return block;
      taskTouched = true;
      itemsHealed += 1;
      return { ...block, data: patched };
    });
    if (!taskTouched) return task;
    touched = true;
    return { ...task, blocks: nextBlocks };
  });
  if (touched) safeInvokeOnHealed(normalizeOpts, 'normalizeSwagUpChoiceBlock', itemsHealed);
  return touched ? { ...tpl, tasks: nextTasks } : tpl;
};

/**
 * AI-generated automation actions have shipped with non-canonical
 * fields (`groupNames: [...]` instead of `group: { id, name }`, no
 * `pplToAdd`, etc.) — these crashed the FE renderer at
 * `a.pplToAdd.map(...)` before the validator could even surface its
 * "Please pick a group" error. Renderers are now defensive, but the
 * persisted shape still needs to converge to canonical so it stops
 * polluting downstream code and so the validator's specific error
 * messages reach the user without the generic "invalid configuration"
 * catch-all firing first.
 *
 * For each action, backfill the type-specific REQUIRED structural
 * fields with empty canonical placeholders (mirrors
 * `buildActionBlock` in functions/ai/src/tools/write/automations.ts).
 * Existing fields are preserved — we only fill gaps, never overwrite.
 * That keeps in-progress admin work intact.
 */
export const normalizeAutomationActions = (
  tpl: IExperienceTemplate,
  opts?: NormalizeOptions
): IExperienceTemplate => {
  let touched = false;
  let itemsHealed = 0;
  const nextAutomations = safeArr<IExperienceTemplate['automations'][number]>(
    tpl?.automations
  ).map((automation) => {
    if (!automation || typeof automation !== 'object') return automation;
    const actions = safeArr<{ type?: string; [k: string]: unknown }>(
      (automation as { actions?: unknown }).actions
    );
    let autoTouched = false;
    const nextActions = actions.map((action: any) => {
      if (!action || typeof action !== 'object') return action;
      const patched: Record<string, unknown> = { ...action };
      let changed = false;
      const ensureObj = (key: string, fallback: Record<string, unknown>) => {
        if (!patched[key] || typeof patched[key] !== 'object') {
          patched[key] = fallback;
          changed = true;
        }
      };
      const ensureArr = (key: string) => {
        if (!Array.isArray(patched[key])) {
          patched[key] = [];
          changed = true;
        }
      };
      const ensureStr = (key: string) => {
        if (typeof patched[key] !== 'string') {
          patched[key] = '';
          changed = true;
        }
      };
      const ensureBool = (key: string, fallback = false) => {
        if (typeof patched[key] !== 'boolean') {
          patched[key] = fallback;
          changed = true;
        }
      };
      // hooks — every action needs this; BlockSettings reads onComplete/onStart
      if (!patched.hooks || typeof patched.hooks !== 'object') {
        patched.hooks = { onStart: [], onComplete: [] };
        changed = true;
      } else {
        const h = patched.hooks as Record<string, unknown>;
        if (!Array.isArray(h.onStart)) {
          patched.hooks = { ...h, onStart: [] };
          changed = true;
        }
        if (!Array.isArray((patched.hooks as { onComplete?: unknown }).onComplete)) {
          patched.hooks = { ...(patched.hooks as object), onComplete: [] };
          changed = true;
        }
      }
      switch ((action as { type?: string }).type) {
        case 'GOOGLE_ADD_TO_GROUP':
        case 'OKTA_ADD_TO_GROUP':
        case 'MS_OUTLOOK_ADD_TO_GROUP':
          ensureObj('group', { id: '', name: '' });
          ensureArr('pplToAdd');
          break;
        case 'SLACK_INVITE_TO_CHANNEL':
        case 'MS_TEAMS_INVITE_TO_CHANNEL':
          ensureObj('channel', { type: 'EXISTING', id: '', name: '', kind: 'PRIVATE' });
          ensureArr('pplToAdd');
          break;
        case 'GOOGLE_CALENDAR_EVENT_INVITATION':
        case 'MS_OUTLOOK_CALENDER_EVENT_INVITATION':
          ensureStr('query');
          ensureObj('calendar', { id: '', name: '' });
          ensureArr('pplToAdd');
          ensureObj('anchor', { type: 'AFTER_EXPERIENCE_START_DATE', lowerBoundary: 0 });
          ensureBool('isRecurring');
          ensureBool('sendNoUpdateEmails');
          break;
        case 'ASANA_CREATE_TASK':
          ensureStr('title');
          ensureObj('workspace', { id: '', name: '' });
          ensureObj('project', { id: '', name: '' });
          break;
        case 'ATLASSIAN_CREATE_ISSUE':
          ensureObj('project', { id: '', name: '' });
          ensureObj('issueType', { id: '', name: '', fields: [] });
          ensureStr('summary');
          ensureArr('fields');
          break;
        case 'LESSONLY_ASSIGN_TO_CONTENT':
          ensureObj('content', { id: '', name: '', type: '' });
          ensureArr('pplToAdd');
          break;
        case 'CREATE_DOCUMENT_FROM_TEMPLATE':
          ensureStr('fileNameFormat');
          // srcTemplate / target stay nullable — admin picks
          break;
        case 'HTTP_POST':
          ensureStr('requestUrl');
          ensureStr('payload');
          break;
        case 'ENROLL_IN_EXPERIENCE':
          ensureStr('actorToEnroll');
          ensureStr('tplToEnrollIn');
          break;
        case 'UNENROLL_FROM_EXPERIENCE':
          break;
      }
      if (!changed) return action;
      autoTouched = true;
      itemsHealed += 1;
      return patched;
    });
    if (!autoTouched) return automation;
    touched = true;
    return { ...automation, actions: nextActions };
  });
  if (touched) safeInvokeOnHealed(opts, 'normalizeAutomationActions', itemsHealed);
  return touched ? { ...tpl, automations: nextAutomations } : tpl;
};

/**
 * `dataChange` timing has THREE legitimate sub-modes per
 * `IItemTemplateTimingDataChange` + the FE's `WhenMode`:
 *
 *   1. watchField=<field>, rules=null/empty
 *        → "When the participant's <field> changes" (any change fires)
 *   2. watchField=<field>, rules=<conditions>
 *        → "When <field> changes AND new value matches conditions"
 *   3. watchField=null,    rules=<conditions>
 *        → "When conditions are met" (no specific field watched)
 *
 * The ONLY structurally-invalid combination is `watchField=null AND
 * no rules` — nothing to listen for. Earlier this validator rejected
 * modes 1 and 3 as broken; that was wrong and would block legitimate
 * AI-generated tasks. Defensive throughout — every dereference is
 * optional-chained / Array.isArray-guarded so a malformed timing shape
 * surfaces this validator's specific message instead of crashing.
 */
const validateDataChangeTimingSelected = (
  timing: {
    type?: string;
    watchField?: string | null;
    rules?: unknown;
  } | null | undefined,
  t: UnsafeTFunction,
  itemLabel: string
): string | null => {
  // Per-function try/catch — same bar as every other validator in this
  // file. The inline guards below already cover null / undefined /
  // wrong-typed fields, but a future Firestore shape could find a way
  // to throw (proxy with a throwing getter, weird inherited proto, …).
  // Swallow and return null so the validator never propagates a throw
  // — the safeValidateItem layer above would catch it anyway, but it'd
  // collapse this whole item to a generic "invalid configuration"
  // message instead of surfacing whatever specific errors the OTHER
  // per-item validators have to say.
  try {
    if (!timing || typeof timing !== 'object') return null;
    if (timing.type !== 'dataChange') return null;
    const hasWatchField =
      typeof timing.watchField === 'string' &&
      timing.watchField.trim().length > 0;
    const rules = timing.rules as { els?: unknown } | null | undefined;
    const hasRules =
      !!rules &&
      typeof rules === 'object' &&
      Array.isArray((rules as { els?: unknown[] }).els) &&
      ((rules as { els: unknown[] }).els.length > 0);
    if (!hasWatchField && !hasRules) {
      return t(
        `This ${itemLabel} is set to "when data changes" but has nothing to listen for — pick a field to watch, or define at least one condition that should trigger it.`
      );
    }
    return null;
  } catch (err) {
    console.warn(
      `[validateDataChangeTimingSelected] threw on ${itemLabel}; returning null so other validators can still surface errors`,
      err
    );
    return null;
  }
};

/**
 * `triggered` timing requires `dependentOn[]` to be non-empty AND each
 * entry to reference a real task on the template (post-resolution by
 * the AI tool, this is structural — but a manual edit OR a stale
 * reference from a deleted task can leave dangling itemIds that the FE
 * silently ignores). Surface as a launch-time error.
 *
 * Defensive: `dependentOn` may be missing/wrong-shape entirely; treat
 * those as "no dependencies set" rather than crashing.
 */
const validateTriggeredTimingSelected = (
  timing: { type?: string; dependentOn?: unknown } | null | undefined,
  tplTasks: Array<{ id?: string }> | undefined,
  t: UnsafeTFunction,
  itemLabel: string
): string | null => {
  // Per-function try/catch — see validateDataChangeTimingSelected for
  // the rationale.
  try {
    if (!timing || typeof timing !== 'object') return null;
    if (timing.type !== 'triggered') return null;
    const deps = Array.isArray(timing.dependentOn)
      ? (timing.dependentOn as Array<{ itemId?: string }>)
      : [];
    if (deps.length === 0) {
      return t(
        `This ${itemLabel} is set to fire "after another task" but no task is selected — pick at least one task it should wait for.`
      );
    }
    const knownIds = new Set(
      (Array.isArray(tplTasks) ? tplTasks : [])
        .filter((task) => task && typeof task === 'object')
        .map((task) => task?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    const dangling = deps
      .map((d) => (d && typeof d === 'object' ? d.itemId : undefined))
      .filter(
        (id): id is string =>
          typeof id === 'string' && id.length > 0 && !knownIds.has(id)
      );
    if (dangling.length > 0) {
      return t(
        `This ${itemLabel} depends on task(s) that don't exist on this template (id${dangling.length > 1 ? 's' : ''}: ${dangling.join(', ')}). Either re-pick the trigger task or remove the orphan dependency.`
      );
    }
    const missingIds = deps.filter(
      (d) =>
        !d ||
        typeof d !== 'object' ||
        typeof (d as { itemId?: unknown }).itemId !== 'string' ||
        ((d as { itemId: string }).itemId).length === 0
    );
    if (missingIds.length > 0) {
      return t(
        `This ${itemLabel} has incomplete trigger entries — every dependency must reference a task. Remove the empty entries or pick a task for each.`
      );
    }
    return null;
  } catch (err) {
    console.warn(
      `[validateTriggeredTimingSelected] threw on ${itemLabel}; returning null so other validators can still surface errors`,
      err
    );
    return null;
  }
};

/**
 * Audience rules can land in two structurally-invalid states:
 *  - `rule: undefined | null` — no rule selected at all.
 *  - `rule: { type: 'all'|'any', els: [] }` — a rule object with no
 *    conditions. The FE's segmentation engine treats this as "everyone"
 *    in practice but the admin clearly intended to filter; it almost
 *    always indicates a half-completed config.
 *
 * Catches both. We do NOT validate what the conditions DO (which field,
 * which operator, which value) — that's semantic correctness and out of
 * scope for this checker.
 */
const validateAudienceRuleSelected = (
  task: IExperienceTemplate['tasks'][number],
  t: UnsafeTFunction
): string[] => {
  const errors: string[] = [];
  const audiences = safeArr<{ id?: string; name?: string; rule?: unknown }>(
    task?.audiences
  ).filter((a) => a && typeof a === 'object');
  for (const audience of audiences) {
    const rule = (audience as { rule?: unknown }).rule;
    const label = audience.name || audience.id || '<unnamed>';
    if (rule == null) {
      errors.push(
        t(
          `Audience "${label}" has no rule — pick "Everyone" or add at least one filter condition.`
        )
      );
      continue;
    }
    if (rule === 'EVERYONE') continue;
    const ruleObj = rule as { type?: string; els?: unknown[] };
    if (!ruleObj.type || !Array.isArray(ruleObj.els) || ruleObj.els.length === 0) {
      errors.push(
        t(
          `Audience "${label}" has no conditions — add at least one filter or set it to "Everyone".`
        )
      );
    }
  }
  return errors;
};

/**
 * `isValidTiming` from ./timing assumes a well-formed timing object —
 * a missing or null `timing` throws on the first `.type` access. Wrap
 * so a malformed timing surfaces as "invalid timing settings" instead
 * of escaping the per-item safety net.
 */
const safeIsValidTiming = (
  timing: unknown
): boolean => {
  try {
    if (timing == null || typeof timing !== 'object') return false;
    return isValidTiming(timing as Parameters<typeof isValidTiming>[0]);
  } catch {
    return false;
  }
};

/**
 * Per-item validators (task / automation / message). Each is wrapped in
 * try/catch at the call site so one structurally-rotten item can't
 * crash the whole Check & Launch flow. Lifted out of the hot loop for
 * readability.
 */
const validateOneTask = (
  task: IExperienceTemplate['tasks'][number],
  tpl: IExperienceTemplate,
  ctx: TaskBuilderContext,
  t: UnsafeTFunction,
  customFields: CustomUserField[]
): string[] => {
  const taskErrors: string[] = [];
  if (!task || typeof task !== 'object') {
    return [t('This task entry is malformed. Remove it from the task list.')];
  }

  if (!task.title) {
    taskErrors.push(t('Task title is required'));
  }

  const actorErr = validateActorReference(task.actor, tpl?.supportingActors, t);
  if (actorErr) taskErrors.push(actorErr);

  // AI-generated tasks can land with `blocks` undefined OR with the
  // wrong shape (a string, an object) if a write went wrong — array
  // methods on those would crash. `safeArr` keeps us iterating.
  const blocks = safeArr<{ type?: string; data?: unknown; id?: string }>(
    task.blocks
  ).filter((b) => b && typeof b === 'object');
  const subTasks = blocks.filter((b) =>
    Object.values(SubTaskType).includes(b.type as SubTaskType)
  );
  if (!subTasks.length) {
    taskErrors.push(t('Task must have at least one subtask'));
  }

  if (!safeIsValidTiming(task.timing)) {
    taskErrors.push(t('Please review your "Start and Deadline" settings'));
  }

  const taskDataChangeErr = validateDataChangeTimingSelected(
    task.timing as { type?: string; watchField?: string | null; rules?: unknown },
    t,
    'task'
  );
  if (taskDataChangeErr) taskErrors.push(taskDataChangeErr);

  const taskTriggeredErr = validateTriggeredTimingSelected(
    task.timing as { type?: string; dependentOn?: unknown },
    tpl?.tasks as Array<{ id?: string }> | undefined,
    t,
    'task'
  );
  if (taskTriggeredErr) taskErrors.push(taskTriggeredErr);

  taskErrors.push(...validateAudienceBlockLinkage(task, t));
  taskErrors.push(...validateAudienceRuleSelected(task, t));

  for (const block of blocks) {
    taskErrors.push(...validateAddressQuestionStorages(block, customFields, t));
  }

  const blockValidation = validateBlocks(blocks as unknown as Parameters<typeof validateBlocks>[0], t, ctx);
  if (!hasValidBlockState(blockValidation)) {
    const fieldErrors = (
      Object.values(blockValidation) as TaskBuilderBlockValidationStateItem[]
    ).flatMap((block) =>
      Object.values(block?.fields ?? {}).filter((v): v is string => !!v)
    );
    taskErrors.push(...fieldErrors);
  }

  return taskErrors;
};

const validateOneAutomation = (
  automation: IExperienceTemplate['automations'][number],
  ctx: TaskBuilderContext,
  t: UnsafeTFunction
): string[] => {
  const autErrors: string[] = [];
  if (!automation || typeof automation !== 'object') {
    return [t('This automation entry is malformed. Remove it.')];
  }

  if (!automation.title) {
    autErrors.push(t('Automation title is required'));
  }

  // AI-generated automations can land with `actions` undefined or
  // wrong-shaped — same protection as blocks.
  const actions: Array<{ type?: string; id?: string }> = safeArr<{
    type?: string;
    id?: string;
  }>(automation.actions).filter((a) => a && typeof a === 'object');
  if (!actions.length) {
    autErrors.push(t('Automation must have at least one action'));
  }

  if (!safeIsValidTiming(automation.timing)) {
    autErrors.push(t('Please review your "Trigger Date" settings'));
  }

  const autDataChangeErr = validateDataChangeTimingSelected(
    automation.timing as { type?: string; watchField?: string | null; rules?: unknown },
    t,
    'automation'
  );
  if (autDataChangeErr) autErrors.push(autDataChangeErr);

  // Automations can be triggered by task completion too — same validation
  // as tasks. We pull the task list from ctx so we don't need to thread
  // tpl in here.
  const autTriggeredErr = validateTriggeredTimingSelected(
    automation.timing as { type?: string; dependentOn?: unknown },
    ctx?.tasks as Array<{ id?: string }> | undefined,
    t,
    'automation'
  );
  if (autTriggeredErr) autErrors.push(autTriggeredErr);

  const actionValidation = validateActions(
    actions as unknown as Parameters<typeof validateActions>[0],
    t,
    ctx,
    automation
  );
  if (!hasValidActionState(actionValidation)) {
    const fieldErrors = (
      Object.values(actionValidation) as AutomationBuilderActionValidationStateItem[]
    ).flatMap((action) =>
      Object.values(action?.fields ?? {}).filter((v): v is string => !!v)
    );
    autErrors.push(...fieldErrors);
  }

  return autErrors;
};

const validateOneMessage = (
  message: { id?: string; subject?: string; actor?: string; timing?: unknown },
  tpl: IExperienceTemplate,
  t: UnsafeTFunction
): string[] => {
  const msgErrors: string[] = [];
  if (!message || typeof message !== 'object') {
    return [t('This message entry is malformed. Remove it.')];
  }

  if (!message.subject) {
    msgErrors.push(t('Message subject is required'));
  }

  const actorErr = validateActorReference(message.actor, tpl?.supportingActors, t);
  if (actorErr) msgErrors.push(actorErr);

  if (!safeIsValidTiming(message.timing)) {
    msgErrors.push(t('Please review your "Send Date" settings'));
  }

  const msgDataChangeErr = validateDataChangeTimingSelected(
    message.timing as { type?: string; watchField?: string | null; rules?: unknown },
    t,
    'message'
  );
  if (msgDataChangeErr) msgErrors.push(msgDataChangeErr);

  const msgTriggeredErr = validateTriggeredTimingSelected(
    message.timing as { type?: string; dependentOn?: unknown },
    tpl?.tasks as Array<{ id?: string }> | undefined,
    t,
    'message'
  );
  if (msgTriggeredErr) msgErrors.push(msgTriggeredErr);

  return msgErrors;
};

/**
 * Wraps a per-item validator in try/catch so one rotten item can't kill
 * the whole list. Lost-context-but-still-flagged is strictly better than
 * silently dropping every other error in the template. The friendly
 * message tells the admin to open and fix or re-create the item.
 */
const safeValidateItem = (
  fn: () => string[],
  itemDesc: string,
  itemId: string,
  t: UnsafeTFunction
): string[] => {
  try {
    return fn();
  } catch (err) {
    console.warn(
      `[validateEntireTemplate] ${itemDesc} ${itemId} threw during validation; surfacing as a structural error`,
      err
    );
    return [
      t(
        `This ${itemDesc} has invalid configuration that can't be checked automatically. Open it and review every field, or remove and re-add it.`
      )
    ];
  }
};

export const validateEntireTemplate = (
  tpl: IExperienceTemplate,
  t: UnsafeTFunction,
  customFields: CustomUserField[] = []
): TemplateValidationError[] => {
  // Outermost guard — if the entire template object is null/undefined
  // (or any unexpected shape that escapes every per-item safety net),
  // return a single template-level error rather than letting the
  // exception propagate up to onCheckAndLaunch / the live re-validation
  // effect. Callers wrap this in their own try/catch too; this is one
  // more belt before those suspenders.
  try {
    return validateEntireTemplateInner(tpl, t, customFields);
  } catch (err) {
    console.warn(
      '[validateEntireTemplate] outer guard caught a throw — returning a single structural error',
      err
    );
    return [
      {
        itemType: 'TASK',
        itemId: '<template>',
        itemTitle: t('Template'),
        errors: [
          t(
            'The template structure is too damaged to validate automatically. Reload the page; if the error persists, contact support.'
          )
        ]
      }
    ];
  }
};

const validateEntireTemplateInner = (
  tpl: IExperienceTemplate,
  t: UnsafeTFunction,
  customFields: CustomUserField[]
): TemplateValidationError[] => {
  const errors: TemplateValidationError[] = [];
  const ctx = buildTaskBuilderContext(tpl);

  // Defensive: `tpl.tasks` / `tpl.automations` / `tpl.messages` are
  // typed as required, but the malformed templates this function exists
  // to validate may not honour the type at runtime. Coerce to empty
  // arrays so the for-loops can't throw.
  const tasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks);
  const automations = safeArr<IExperienceTemplate['automations'][number]>(
    tpl?.automations
  );

  // Validate tasks — each in its own try/catch so a single structurally
  // broken task surfaces as one error in the list instead of killing
  // validation for all subsequent tasks/automations/messages.
  for (const task of tasks) {
    const taskErrors = safeValidateItem(
      () => validateOneTask(task, tpl, ctx, t, customFields),
      'task',
      task?.id ?? '<unknown>',
      t
    );

    if (taskErrors.length) {
      errors.push({
        itemType: 'TASK',
        itemId: task?.id ?? '<unknown>',
        itemTitle: task?.title || t('Untitled Task'),
        errors: taskErrors
      });
    }
  }

  // Validate automations
  for (const automation of automations) {
    const autErrors = safeValidateItem(
      () => validateOneAutomation(automation, ctx, t),
      'automation',
      automation?.id ?? '<unknown>',
      t
    );

    if (autErrors.length) {
      errors.push({
        itemType: 'AUTOMATION',
        itemId: automation?.id ?? '<unknown>',
        itemTitle: automation?.title || t('Untitled Automation'),
        errors: autErrors
      });
    }
  }

  // Validate messages — `getAllMessageTemplates` can itself throw on a
  // malformed `tpl.messages` shape, so guard the gather step too.
  let messages: ReturnType<typeof getAllMessageTemplates> = [];
  try {
    messages = tpl?.messages ? getAllMessageTemplates(tpl.messages) : [];
  } catch (err) {
    console.warn(
      `[validateEntireTemplate] getAllMessageTemplates threw; treating template as having no messages`,
      err
    );
  }
  for (const message of messages) {
    const msgErrors = safeValidateItem(
      () => validateOneMessage(message, tpl, t),
      'message',
      message?.id ?? '<unknown>',
      t
    );

    if (msgErrors.length) {
      errors.push({
        itemType: 'MESSAGE',
        itemId: message?.id ?? '<unknown>',
        itemTitle: message?.subject || t('Untitled Message'),
        errors: msgErrors
      });
    }
  }

  return errors;
};

// ─────────────────────────────────────────────────────────────────────
// Validation Checklist — additive, structured summary for the
// "Check & Launch" / "Save" surfaces. NEVER touches or weakens
// validateEntireTemplate; consumes its output and the raw template,
// returns a 5-section grouped object the UI can render verbatim.
//
// Sections (fixed order — see chat-assistant-spec.md for the
// canonical wording and the corresponding chat output):
//   1. Summary — counts + anchor + actors actually in use
//   2. Actors to review — each actor with type + next-step guidance
//   3. Content gaps — empty bodies, missing files/URLs (subset of
//      validateEntireTemplate errors filtered to user-fillable
//      content placeholders)
//   4. Integration warnings — blocks/automations/channels that
//      reference inactive integrations
//   5. Next steps — preview, test enrollment, notification sender;
//      AND an ALWAYS-ON "validate audiences yourself" reminder
//      (our automated validation doesn't cover audience rules)
//
// "Advanced triggers" (dataChange / relativeToDeadlineOfTrigger) is
// intentionally skipped for now — see Beads ticket for follow-up.
//
// Resilience guarantees:
//   - Every section's computation runs inside its own try/catch.
//   - A throwing section returns an empty array, NOT an error to the
//     caller. The checklist is best-effort; partial output is better
//     than no output.
//   - Empty sections (no entries) are FILTERED OUT before return.
//     Summary always renders (counts default to 0 on malformed input);
//     Next steps always renders (audience reminder is mandatory).
// ─────────────────────────────────────────────────────────────────────

export interface ChecklistSummary {
  taskCount: number;
  messageCount: number;
  automationCount: number;
  anchorLabel: string | null;
  actorsInUse: string[];
}

export interface ChecklistActorEntry {
  key: string;
  name: string;
  type: 'user' | 'staticUser' | 'assignedBySubtask' | 'unknown';
  nextStep: string;
}

export interface ChecklistContentGap {
  itemType: 'TASK' | 'MESSAGE' | 'AUTOMATION';
  itemId: string;
  itemTitle: string;
  description: string;
}

export interface ChecklistIntegrationWarning {
  source: 'SUBTASK' | 'AUTOMATION' | 'MESSAGE_CHANNEL';
  itemTitle: string;
  requiredIntegration: string;
  detail: string;
}

export interface ChecklistNextStep {
  /** Stable key for tests / future help-link wiring. */
  id: 'audiences' | 'preview' | 'testEnrollment' | 'notificationSender';
  title: string;
  body: string;
  /** Optional help-article URL. When present, the renderer surfaces
   * it as a "Learn more" link next to the step. Tracked in Beads
   * new-main-kun9 — currently one general URL pending per-section
   * authoring. */
  helpUrl?: string;
}

export interface ValidationChecklist {
  summary: ChecklistSummary;
  actorsToReview: ChecklistActorEntry[];
  contentGaps: ChecklistContentGap[];
  integrationWarnings: ChecklistIntegrationWarning[];
  nextSteps: ChecklistNextStep[];
}

/** Optional org-side signal passed in by the FE caller. When omitted
 * (default: empty Set), Integration warnings won't fire for inactive
 * integrations — the section just stays empty. Callers that have the
 * info SHOULD pass it; callers that don't can safely omit. */
export interface ValidationChecklistOptions {
  /** Lowercase or canonical integration keys that ARE active on this
   * org (e.g. new Set(['SLACK', 'DOCUSIGN'])). */
  activeIntegrations?: Set<string>;
}

const safeSection = <T>(fn: () => T[], label: string): T[] => {
  try {
    return fn();
  } catch (err) {
    console.warn(`[buildValidationChecklist] ${label} threw; returning []`, err);
    return [];
  }
};

const ANCHOR_TYPE_LABELS: Record<string, string> = {
  EMPLOYEE_DATE: 'Employee date',
  EMPLOYEE_FIELD: 'Employee field',
  FIXED_DATE: 'Fixed date',
  EXPERIENCE_START: 'Experience start',
};

// Integration keys here match the `Integration` enum values from
// @dadohr/shared/domainTypes/integrations (which is what
// `useIntegrationsContext().active` returns — a Set<Integration>).
// Stay aligned with that enum so the FE caller can pass
// `new Set([...activeIntegrations])` directly without remapping.
//
// NOTE: AI-side write tools (functions/ai/src/tools/types.ts) use a
// SEPARATE uppercase-string set of integration keys that match the
// orgIntegrationsV1/{orgId}.configInfos document key shape. The two
// surfaces don't need to share keys — they read different sources.
const SUBTASK_REQUIRED_INTEGRATION: Record<string, string> = {
  [SubTaskType.CALENDLY_INVITE]: Integration.CALENDLY,
  [SubTaskType.DOCUSIGN_ENVELOPE_SIGN]: Integration.DOCUSIGN,
  [SubTaskType.SWAG_UP_CHOICE]: Integration.SWAG_UP,
};

const AUTOMATION_REQUIRED_INTEGRATION: Record<string, string> = {
  SLACK_INVITE_TO_CHANNEL: Integration.SLACK,
  MS_TEAMS_INVITE_TO_CHANNEL: Integration.MICROSOFT,
  // The apps Integration enum has no dedicated GOOGLE_CALENDAR /
  // GOOGLE_DOCS entries today — using the unmapped key means the
  // active-set lookup never matches and these automation types
  // always flag, which is wrong when Google IS configured. Keep
  // the keys here so the warning surface is *aware* of the type,
  // but use a sentinel that callers can adapt. For now we pass
  // empty string so the lookup harmlessly never matches — the
  // alternative (flagging every Google action even when configured)
  // would be a false positive.
  // TODO: when Integration enum grows GOOGLE_CALENDAR / GOOGLE_DOCS,
  // swap these to the proper values. Tracked in Beads.
  GOOGLE_CALENDAR_EVENT_INVITATION: '',
  MS_OUTLOOK_CALENDER_EVENT_INVITATION: Integration.MICROSOFT,
  GOOGLE_ADD_TO_GROUP: '',
  MS_OUTLOOK_ADD_TO_GROUP: Integration.MICROSOFT,
  OKTA_ADD_TO_GROUP: Integration.OKTA_API,
  ASANA_CREATE_TASK: Integration.ASANA,
  ATLASSIAN_CREATE_ISSUE: Integration.ATLASSIAN,
  LESSONLY_ASSIGN_TO_CONTENT: Integration.LESSONLY,
  CREATE_DOCUMENT_FROM_TEMPLATE: '',
};

const MESSAGE_CHANNEL_REQUIRED_INTEGRATION: Record<string, string> = {
  SLACK: Integration.SLACK,
  TEAMS: Integration.MICROSOFT,
};

const computeSummary = (tpl: IExperienceTemplate): ChecklistSummary => {
  const tasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks);
  const automations = safeArr<unknown>(tpl?.automations);
  const messageBag = tpl?.messages as
    | { intro?: unknown; outro?: unknown; other?: unknown }
    | undefined;
  const messageCount =
    (messageBag?.intro ? 1 : 0) +
    (messageBag?.outro ? 1 : 0) +
    safeArr<unknown>(messageBag?.other).length;

  // Anchor — show type + field when present.
  let anchorLabel: string | null = null;
  const anchor = tpl?.anchor as { type?: string; field?: string } | undefined;
  if (anchor?.type) {
    const typeLabel = ANCHOR_TYPE_LABELS[anchor.type] ?? anchor.type;
    anchorLabel = anchor.field ? `${typeLabel} (${anchor.field})` : typeLabel;
  }

  // Actors in use — derived from ACTUAL usage in tasks / messages /
  // automations, not from the supportingActors map. An actor that
  // exists in supportingActors but isn't referenced is dead weight
  // and shouldn't surface to the admin as "in use".
  const usedActors = new Set<string>();
  for (const task of tasks) {
    if (typeof task?.actor === 'string') usedActors.add(task.actor);
  }
  for (const msg of safeArr<{ actor?: unknown }>(messageBag?.other)) {
    if (typeof msg?.actor === 'string') usedActors.add(msg.actor);
  }
  if (messageBag?.intro && typeof (messageBag.intro as { actor?: unknown }).actor === 'string') {
    usedActors.add((messageBag.intro as { actor: string }).actor);
  }
  if (messageBag?.outro && typeof (messageBag.outro as { actor?: unknown }).actor === 'string') {
    usedActors.add((messageBag.outro as { actor: string }).actor);
  }
  // Sort: 'participant' first, then alphabetical.
  const actorsInUse = Array.from(usedActors).sort((a, b) => {
    if (a === PARTICIPANT_ACTOR_KEY) return -1;
    if (b === PARTICIPANT_ACTOR_KEY) return 1;
    return a.localeCompare(b);
  });

  return {
    taskCount: tasks.length,
    messageCount,
    automationCount: automations.length,
    anchorLabel,
    actorsInUse,
  };
};

const ACTOR_NEXT_STEP_BY_TYPE: Record<string, string> = {
  user: 'Verify the profile field is populated for every participant who will be enrolled.',
  staticUser: 'Assign a specific person in Settings > Actors — currently unassigned.',
  assignedBySubtask:
    'Make sure the assignment subtask fires before any task or message that references this actor.',
  unknown: 'Open Settings > Actors and confirm the configuration.',
};

const computeActorsToReview = (
  tpl: IExperienceTemplate,
  usedActorKeys: string[]
): ChecklistActorEntry[] => {
  const supportingActors = (tpl?.supportingActors ?? {}) as Record<
    string,
    { name?: unknown; type?: unknown }
  >;
  const entries: ChecklistActorEntry[] = [];
  for (const key of usedActorKeys) {
    if (key === PARTICIPANT_ACTOR_KEY) continue; // participant has no "review" surface
    const actor = supportingActors[key];
    // If an actor is referenced but missing from supportingActors,
    // validateEntireTemplate already flagged it — we still list it
    // here as 'unknown' so the admin sees what needs adding.
    const type: ChecklistActorEntry['type'] =
      actor && typeof actor.type === 'string' &&
      (actor.type === 'user' || actor.type === 'staticUser' || actor.type === 'assignedBySubtask')
        ? actor.type
        : 'unknown';
    const name =
      actor && typeof actor.name === 'string' && actor.name.trim() ? actor.name : key;
    entries.push({
      key,
      name,
      type,
      nextStep: ACTOR_NEXT_STEP_BY_TYPE[type] ?? ACTOR_NEXT_STEP_BY_TYPE.unknown,
    });
  }
  return entries;
};

const computeContentGaps = (tpl: IExperienceTemplate): ChecklistContentGap[] => {
  const gaps: ChecklistContentGap[] = [];
  const tasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks);

  for (const task of tasks) {
    const blocks = safeArr<{ type?: unknown; data?: any }>(task?.blocks);
    for (const block of blocks) {
      if (!block || typeof block !== 'object') continue;
      const data = (block.data ?? {}) as Record<string, unknown>;
      const blockType = block.type;
      const taskTitle = task?.title || '<untitled task>';

      // VISIT / WATCH / CALENDLY_INVITE → url required
      if (
        (blockType === SubTaskType.VISIT ||
          blockType === SubTaskType.WATCH ||
          blockType === SubTaskType.CALENDLY_INVITE) &&
        (typeof data.url !== 'string' || !data.url.trim())
      ) {
        gaps.push({
          itemType: 'TASK',
          itemId: task?.id ?? '<unknown>',
          itemTitle: taskTitle,
          description: `${blockType} subtask "${
            (data.label as string | undefined) || '<unnamed>'
          }" has no URL set.`,
        });
      }

      // DOCUMENT_READ → docUrl required
      if (
        blockType === SubTaskType.DOCUMENT_READ &&
        (typeof data.docUrl !== 'string' || !data.docUrl.trim())
      ) {
        gaps.push({
          itemType: 'TASK',
          itemId: task?.id ?? '<unknown>',
          itemTitle: taskTitle,
          description: `DOCUMENT_READ subtask "${
            (data.title as string | undefined) || '<unnamed>'
          }" has no document attached.`,
        });
      }

      // DOCUSIGN_ENVELOPE_SIGN → description.t required (canonical
      // shape healed on launch, but admins can blank it out later)
      if (
        blockType === SubTaskType.DOCUSIGN_ENVELOPE_SIGN &&
        (typeof (data.description as { t?: unknown })?.t !== 'string' ||
          !((data.description as { t?: string }).t ?? '').trim())
      ) {
        gaps.push({
          itemType: 'TASK',
          itemId: task?.id ?? '<unknown>',
          itemTitle: taskTitle,
          description: `DocuSign subtask in "${taskTitle}" has no description for the participant.`,
        });
      }

      // SWAG_UP_CHOICE → choices array must be non-empty
      if (
        blockType === SubTaskType.SWAG_UP_CHOICE &&
        (!Array.isArray(data.choices) || data.choices.length === 0)
      ) {
        gaps.push({
          itemType: 'TASK',
          itemId: task?.id ?? '<unknown>',
          itemTitle: taskTitle,
          description: `SwagUp subtask in "${taskTitle}" has no swag options configured.`,
        });
      }
    }
  }

  // Messages — empty body OR empty subject is a gap.
  const messageBag = tpl?.messages as
    | { intro?: unknown; outro?: unknown; other?: unknown }
    | undefined;
  const allMessages: Array<{ id?: unknown; subject?: unknown; body?: unknown }> = [];
  if (messageBag?.intro && typeof messageBag.intro === 'object') {
    allMessages.push(messageBag.intro as { subject?: unknown; body?: unknown });
  }
  if (messageBag?.outro && typeof messageBag.outro === 'object') {
    allMessages.push(messageBag.outro as { subject?: unknown; body?: unknown });
  }
  for (const m of safeArr<{ id?: unknown; subject?: unknown; body?: unknown }>(messageBag?.other)) {
    allMessages.push(m);
  }
  for (const msg of allMessages) {
    const subject = typeof msg.subject === 'string' ? msg.subject : '';
    const bodyText =
      msg.body && typeof msg.body === 'object'
        ? ((msg.body as { t?: unknown }).t as string | undefined) ?? ''
        : '';
    const id = typeof msg.id === 'string' ? msg.id : '<unknown>';
    if (!subject.trim()) {
      gaps.push({
        itemType: 'MESSAGE',
        itemId: id,
        itemTitle: subject || '<no subject>',
        description: 'Message has no subject.',
      });
    }
    if (!bodyText.trim()) {
      gaps.push({
        itemType: 'MESSAGE',
        itemId: id,
        itemTitle: subject || '<no subject>',
        description: 'Message has an empty body — replace the placeholder before launch.',
      });
    }
  }

  return gaps;
};

const computeIntegrationWarnings = (
  tpl: IExperienceTemplate,
  active: Set<string>
): ChecklistIntegrationWarning[] => {
  const warnings: ChecklistIntegrationWarning[] = [];
  const tasks = safeArr<IExperienceTemplate['tasks'][number]>(tpl?.tasks);

  // Subtask types gated by integration. Empty-string `required`
  // (see TODO above) means the apps Integration enum doesn't have an
  // entry for that integration yet — skip rather than always-flag.
  for (const task of tasks) {
    const blocks = safeArr<{ type?: unknown }>(task?.blocks);
    for (const block of blocks) {
      const type = typeof block?.type === 'string' ? block.type : undefined;
      if (!type) continue;
      const required = SUBTASK_REQUIRED_INTEGRATION[type];
      if (required && required.length > 0 && !active.has(required)) {
        warnings.push({
          source: 'SUBTASK',
          itemTitle: task?.title || '<untitled task>',
          requiredIntegration: required,
          detail: `${type} subtask requires the ${required} integration (not active).`,
        });
      }
    }
  }

  // Automation actions gated by integration
  for (const auto of safeArr<{ title?: unknown; actions?: unknown }>(tpl?.automations)) {
    const title = typeof auto?.title === 'string' ? auto.title : '<untitled automation>';
    for (const action of safeArr<{ type?: unknown }>(auto?.actions)) {
      const type = typeof action?.type === 'string' ? action.type : undefined;
      if (!type) continue;
      const required = AUTOMATION_REQUIRED_INTEGRATION[type];
      if (required && required.length > 0 && !active.has(required)) {
        warnings.push({
          source: 'AUTOMATION',
          itemTitle: title,
          requiredIntegration: required,
          detail: `Automation action ${type} requires the ${required} integration (not active).`,
        });
      }
    }
  }

  // Message channels (SLACK/TEAMS)
  const messageBag = tpl?.messages as
    | { intro?: unknown; outro?: unknown; other?: unknown }
    | undefined;
  const allMessagesForChannels: Array<{ subject?: unknown; channels?: unknown }> = [];
  if (messageBag?.intro && typeof messageBag.intro === 'object') {
    allMessagesForChannels.push(messageBag.intro as { subject?: unknown; channels?: unknown });
  }
  if (messageBag?.outro && typeof messageBag.outro === 'object') {
    allMessagesForChannels.push(messageBag.outro as { subject?: unknown; channels?: unknown });
  }
  for (const m of safeArr<{ subject?: unknown; channels?: unknown }>(messageBag?.other)) {
    allMessagesForChannels.push(m);
  }
  for (const msg of allMessagesForChannels) {
    const subject = typeof msg?.subject === 'string' ? msg.subject : '<no subject>';
    for (const ch of safeArr<{ type?: unknown }>(msg?.channels)) {
      const type = typeof ch?.type === 'string' ? ch.type : undefined;
      if (!type) continue;
      const required = MESSAGE_CHANNEL_REQUIRED_INTEGRATION[type];
      if (required && required.length > 0 && !active.has(required)) {
        warnings.push({
          source: 'MESSAGE_CHANNEL',
          itemTitle: subject,
          requiredIntegration: required,
          detail: `Message "${subject}" uses channel ${type} which requires the ${required} integration (not active).`,
        });
      }
    }
  }

  return warnings;
};

// Help URLs — see Beads new-main-kun9. Per-section articles still
// pending; we have two sourced so far. The renderer surfaces these
// as "Learn more" links next to each next-step item.
const HELP_URL_TARGET_AUDIENCES = 'https://help.dadohr.com/article/31-target-audiences';
const HELP_URL_HOW_TO_TEST = 'https://help.dadohr.com/article/121-how-to-test-your-dado-experience';

const NEXT_STEPS_FIXED: ChecklistNextStep[] = [
  {
    id: 'audiences',
    title: 'Audiences',
    body:
      "Our tooling can't validate audience rules yet. Open each task and message and confirm the audience matches who should receive it (location, department, custom group). Always verify this — even when the rest of the checklist is clean.",
    helpUrl: HELP_URL_TARGET_AUDIENCES,
  },
  {
    id: 'preview',
    title: 'Preview',
    body: 'Click Preview to see the participant view before launching.',
    helpUrl: HELP_URL_HOW_TO_TEST,
  },
  {
    id: 'testEnrollment',
    title: 'Test enrollment',
    body: 'Enroll a test user before going live to confirm the end-to-end flow.',
    helpUrl: HELP_URL_HOW_TO_TEST,
  },
  {
    id: 'notificationSender',
    title: 'Notification sender',
    body: 'Settings > Notifications — confirm the sender name and reply-to.',
    // No helpUrl — the available articles are about audiences /
    // testing, not notification sender configuration.
  },
];

/**
 * Build the structured 5-section checklist. Safe to call with
 * malformed input — each section catches its own errors and falls
 * back to an empty array. Sections are returned in canonical order;
 * empty sections (except Summary + Next steps, which always render)
 * appear as empty arrays so the renderer can filter them out
 * uniformly.
 *
 * `validationErrors` is consumed for future cross-referencing — for
 * now we derive content gaps directly from the template (so this
 * function is independent of the validator's exact error wording).
 * Kept in the signature so a future iteration can grow on top
 * without a breaking change.
 */
export const buildValidationChecklist = (
  tpl: IExperienceTemplate,
  _validationErrors: TemplateValidationError[] = [],
  options: ValidationChecklistOptions = {}
): ValidationChecklist => {
  const active = options.activeIntegrations ?? new Set<string>();
  // Summary first — its actorsInUse list feeds Actors to review.
  let summary: ChecklistSummary;
  try {
    summary = computeSummary(tpl);
  } catch (err) {
    console.warn('[buildValidationChecklist] computeSummary threw', err);
    summary = {
      taskCount: 0,
      messageCount: 0,
      automationCount: 0,
      anchorLabel: null,
      actorsInUse: [],
    };
  }
  return {
    summary,
    actorsToReview: safeSection(
      () => computeActorsToReview(tpl, summary.actorsInUse),
      'actorsToReview'
    ),
    contentGaps: safeSection(() => computeContentGaps(tpl), 'contentGaps'),
    integrationWarnings: safeSection(
      () => computeIntegrationWarnings(tpl, active),
      'integrationWarnings'
    ),
    // Next steps is intentionally a copy of NEXT_STEPS_FIXED — the
    // audience bullet is MANDATORY (per the ticket: "always mention to
    // the user to validate target audiences since our tool can't do
    // that yet"). The other three bullets are universal launch advice.
    nextSteps: [...NEXT_STEPS_FIXED],
  };
};
